import { useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import ControlBar from "ol-ext/control/Bar";
import Button from "ol-ext/control/Button";
import logger from '../logger';
import "ol/ol.css";
import "ol-ext/dist/ol-ext.css";
import '@fortawesome/fontawesome-free/css/all.min.css';
import "../App.css";

const SideBar = ({
  mapRef,
  isLoggedIn,
  setIsLoggedIn,
  setHoveredButtonInfo,
  toggleDrawer
}) => {
  const controlBarRef = useRef(null);
  const navigate = useNavigate();

    const removeInfoElement = () => {
        const infoElement = document.getElementById('info-element');
        if (infoElement) {
            infoElement.remove();
        }
    };

  // Check if running inside an iFrame
  const isInIframe = window !== window.parent;

  useEffect(() => {
    if (!mapRef.current) return;

    // Store the current mapRef in a local variable
    const map = mapRef.current;

    const infoButton = new Button({
        html: '<i class="fas fa-info-circle"></i>',  // Icon for the info button
        title: 'Information',
        className: 'sidebar-section',
        handleClick: toggleDrawer,  // When clicked, the drawer toggles
    });

    // Add hover events to info button
    infoButton.element.addEventListener('mouseenter', () => {
        setHoveredButtonInfo('<i class="fas fa-info-circle"></i> <strong>Information</strong>: Click to toggle the information window. This button will open or close the drawer, providing you with details about the functionality of other controls.');
    });
    infoButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    const zoomInButton = new Button({
        html: '<i class="fa fa-plus" aria-hidden="true"></i>',
        title: "Zoom in",
        handleClick: function () {
            const view = map.getView();
            const zoom = view.getZoom();
            view.setZoom(zoom + 1);
        },
    });

    // Add hover events to zoom in button
    zoomInButton.element.addEventListener('mouseenter', () => {
        setHoveredButtonInfo('<i class="fa fa-plus" aria-hidden="true"></i> <strong>Zoom In:</strong> Click to zoom in on the map for a closer, more detailed view of the area.');
    });
    zoomInButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    const zoomOutButton = new Button({
        html: '<i class="fa fa-minus"></i>',
        title: "Zoom out",
        className: "sidebar-section",
        handleClick: function () {
            const view = map.getView();
            const zoom = view.getZoom();
            view.setZoom(zoom - 1);
        },
    });

    // Add hover events to zoom out button
    zoomOutButton.element.addEventListener('mouseenter', () => {
        setHoveredButtonInfo('<i class="fa fa-minus"></i> <strong>Zoom Out:</strong> Click to zoom out from the map, providing a wider view of the surrounding area.');
    });
    zoomOutButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    const goToUserDefinedData = () => {
        removeInfoElement();
        navigate('/user-defined-data');
    };
  
    const userDefinedButton = new Button({
    html: '<i class="fas fa-database"></i>',
    title: 'User Defined Data',
    handleClick: goToUserDefinedData,
    });

    // Add hover events to go to home button
    userDefinedButton.element.addEventListener('mouseenter', () => {
        setHoveredButtonInfo('<i class="fas fa-database"></i> <strong>User Defined Data:</strong> Click to go to the tool for User-Defined-Data.');
    });
    userDefinedButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    const goToNonFuel = () => {
        removeInfoElement();
        navigate('/non-fuel-areas');
    };
  
    const nonFuelButton = new Button({
    html: '<i class="fas fa-binoculars"></i>',
    title: 'Go to Non-Fuel-Areas Page',
    handleClick: goToNonFuel,
    });

    // Add hover events to non-fuel button
    nonFuelButton.element.addEventListener('mouseenter', () => {
    setHoveredButtonInfo('<i class="fas fa-binoculars"></i> <strong>Non Fuel Areas:</strong> Click to navigate to the Non-Fuel-Areas tool, which allows you to view and analyze areas that don\'t support wildfires.');
    });
    nonFuelButton.element.addEventListener('mouseleave', () => {
    setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    const goToMonitoring = () => {
        removeInfoElement();
        navigate('/monitoring');
    };

    const monitoringButton = new Button({
        html: '<i class="far fa-eye"></i>',
        title: 'Monitoring',
        // className: "sidebar-section",
        handleClick: goToMonitoring,
    });

    // Add hover events to monitoring button
    monitoringButton.element.addEventListener('mouseenter', () => {
        setHoveredButtonInfo('<i class="far fa-eye"></i> <strong>Monitoring:</strong> Click to open the Monitoring tool, where you can track and monitor specific features on the map.');
    });
    monitoringButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    function goToSharePosition() {
        // Navigate to /share-position first
        removeInfoElement();
        navigate('/share-position');

    };

    const sharePositionButton = new Button({
        html: '<i class="fas fa-crosshairs"></i>',
        title: 'Share Position',
        className: "sidebar-section",
        handleClick: goToSharePosition,
    });

    // Add hover events to timming button
    sharePositionButton.element.addEventListener('mouseenter', () => {
        setHoveredButtonInfo('<i class="far fa-eye"></i> <strong>Share Position:</strong> Click to open the Share Position tool and save your current coordinates at specific timestamps.');
    });
    sharePositionButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    const handleLogout = () => {
        // Clear tokens and update login state
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        setIsLoggedIn(false);

        // Update the account button (replace icon with logout icon)
        accountButton.setHtml('<i class="fas fa-user"></i>'); // Set it to the login icon
        removeInfoElement();
        navigate('/login'); // Navigate to the homepage or refresh
        //logger.log("logout clicked");
    };

    const accountButton = new Button({
        html: isLoggedIn ? '<i class="fas fa-sign-out-alt"></i>' : '<i class="fas fa-user"></i>',
        title: isLoggedIn ? "Log Out" : "Log In",
        handleClick: () => {
            if (isLoggedIn) {
                handleLogout();  // Call the logout handler
            } else {
                navigate('/login');  // Navigate to login page
            }
        },
    });

    // Re-create the account button based on login state
    accountButton.setHtml(isLoggedIn ? '<i class="fas fa-sign-out-alt"></i>' : '<i class="fas fa-user"></i>');

    // Add event listeners for mouse enter and leave
    accountButton.element.addEventListener('mouseenter', () => {
        const iconHtml = isLoggedIn
            ? '<i class="fas fa-sign-out-alt"></i> <strong>Log Out:</strong> Click to log out of your account.'
            : '<i class="fas fa-user"></i> <strong>Log In:</strong> Click to log in to your account and access additional features.';
        setHoveredButtonInfo(iconHtml);
    });

    accountButton.element.addEventListener('mouseleave', () => {
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
    });

    // Controls Array (Conditionally add account button based on iframe detection)
    const controls = [
      infoButton,
      zoomInButton,
      zoomOutButton,
      userDefinedButton,
      nonFuelButton,
      monitoringButton,
      sharePositionButton,
    ];

    if (!isInIframe) {
      controls.push(accountButton);
    }

    // Add Sidebar to Map
    const sideBarControlBar = new ControlBar({
      controls,
      orientation: "vertical",
      className: "control-bar",
    });

    sideBarControlBar.setPosition("top-left");
    map.addControl(sideBarControlBar);
    controlBarRef.current = sideBarControlBar;

    logger.log("Props in SideBar:", { mapRef, isLoggedIn, setIsLoggedIn, setHoveredButtonInfo, toggleDrawer });

    logger.log(`Sidebar initialized. isInIframe: ${isInIframe}`);

    return () => {
      if (map && controlBarRef.current) {
        map.removeControl(controlBarRef.current);
      }
    };
  }, [mapRef, isLoggedIn, navigate]);

  return null;
};

export default SideBar;
